/** @jsx isolateComponent */

import { Fragment } from 'react';

import { AsyncIndicator } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Link } from '@zapier/design-system';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import { buildUpgradeUrl } from '@zapier/design-system-revenue/src/utils';

import { useData as _useData } from './hooks/useData';
import {
  Props as ShouldRenderProps,
  useShouldRender,
} from './hooks/useShouldRender';

type Props = {
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

const CompanyTrialEndedBanner = ({ useData = _useData }: Props) => {
  const { currentPlan, error, isLoading, nextPlan, profileId } = useData();

  const { trackEvent } = useTrackingContext();

  if (isLoading) {
    return <AsyncIndicator />;
  }

  if (error) {
    return <Fragment>{error}</Fragment>;
  }

  if (!currentPlan || !nextPlan) {
    return null;
  }

  const onUpgradeClick = () => {
    trackEvent('company-trial-ended-upgrade-clicked', {
      profileId,
    });
  };

  const onSupportClick = () => {
    trackEvent('company-trial-ended-support-extend-trial-clicked', {
      profileId,
    });
  };

  return (
    <Fragment>
      Your Zapier for Companies trial has ended.{' '}
      <Link
        color="white"
        href={buildUpgradeUrl(
          currentPlan.name,
          nextPlan.name,
          nextPlan.interval
        )}
        onClick={onUpgradeClick}
      >
        <span style={{ textDecoration: 'underline' }}>Upgrade now</span>
      </Link>{' '}
      to continue using our advanced administrative and security controls, or{' '}
      <Link color="white" href="/app/get-help" onClick={onSupportClick}>
        <span style={{ textDecoration: 'underline' }}>contact support</span>
      </Link>{' '}
      to extend your trial.
    </Fragment>
  );
};

CompanyTrialEndedBanner.shouldRender = function shouldRender(
  props: ShouldRenderProps = {}
) {
  return useShouldRender.call(this, props);
};

export { CompanyTrialEndedBanner };
